// Vendor
import { createClient, type CreateClientParams } from "contentful";

// Constants
import {
  CONTENTFUL_LOCALE,
  CONTENTFUL_SPACE_NAME,
} from "@citizenm/foundation/shared/types/contentful/constants";

// Types
import type {
  AppLocale,
  ContentfulSpaceName,
} from "@citizenm/foundation/shared/types/contentful";

/**
 *
 * @deprecated This Contentful client is used for Product and Marketing spaces. New implementations rely on the Remember space and ditch the runtime config approach.
 */
export function createContentfulClient(
  space: ContentfulSpaceName = CONTENTFUL_SPACE_NAME.Product,
  preview = false,
) {
  const { public: publicConfig } = useRuntimeConfig();

  const {
    // Product
    ctfSpaceId,
    ctfEnvironment,
    ctfCdaAccessToken,
    ctfCdaPreviewToken,
    // Marketing
    ctfMarketingSpaceId,
    ctfMarketingEnvironment,
    ctfMarketingCdaAccessToken,
    ctfMarketingCdaPreviewToken,
  } = publicConfig;

  const config: CreateClientParams =
    space === CONTENTFUL_SPACE_NAME.Marketing
      ? // Marketing config
        {
          accessToken: preview
            ? String(ctfMarketingCdaPreviewToken)
            : String(ctfMarketingCdaAccessToken),
          environment: String(ctfMarketingEnvironment),
          headers: {
            "x-contentful-resource-resolution": btoa(
              JSON.stringify({
                spaces: {
                  [String(ctfSpaceId)]: preview
                    ? ctfCdaPreviewToken
                    : ctfCdaAccessToken,
                },
              }),
            ),
          },
          host: `${preview ? "preview" : "cdn"}.contentful.com`,
          space: String(ctfMarketingSpaceId),
        }
      : // Product config
        {
          accessToken: String(ctfCdaAccessToken),
          environment: String(ctfEnvironment),
          space: String(ctfSpaceId),
        };

  return createClient(config);
}

/**
 * Does a reverse lookup of the locale key by value on the CONTENTFUL_LOCALE constant.
 *
 * @param {locale} - the locale to get the key for
 * @param {fallBackLocale} - the fallback locale to use if the locale is not found
 *
 * @returns {string} - the key for the locale
 */
export function getLocaleKeyByValue(
  locale: string,
  fallBackLocale = CONTENTFUL_LOCALE.en,
) {
  return (Object.entries(CONTENTFUL_LOCALE).find(
    ([, v]) => v === locale,
  )?.[0] || fallBackLocale) as AppLocale;
}
